// ============================================================
// INVISIBLE ORCHESTRATION LAYER
// Intent Detection -> Capability Router -> Response Synthesizer
// The user never sees agents. Miraal just DOES things.
// ============================================================

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// ============================================================
// TYPES
// ============================================================

export type Capability =
  | 'analytics' | 'content' | 'automation' | 'research'
  | 'security' | 'ml' | 'ux' | 'market_research'
  | 'dashboard' | 'performance' | 'general';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface IntentResult {
  primaryIntent: string;
  capabilities: Capability[];
  confidence: number;
  entities: Record<string, any>;
  urgency: 'low' | 'medium' | 'high';
  requiresFollowUp: boolean;
}

export interface CapabilityResult {
  capability: Capability;
  output: any;
  latency: number;
  success: boolean;
  error?: string;
}

export interface OrchestratedResponse {
  text: string;
  inlineCapability?: {
    type: string;
    data: any;
    config?: any;
  };
  suggestedActions: Array<{
    id: string;
    label: string;
    action: string;
  }>;
  capabilitiesUsed: Capability[];
}

export interface OrchestratorState {
  isProcessing: boolean;
  currentIntent: IntentResult | null;
  activeCapabilities: Capability[];
  lastResponse: OrchestratedResponse | null;
  error: string | null;
}

// ============================================================
// INTENT DETECTION ENGINE
// Keyword + pattern + context-based classification
// No LLM call needed for routing -- fast, silent, cheap
// ============================================================

const INTENT_PATTERNS: Array<{
  intent: string;
  capabilities: Capability[];
  patterns: RegExp[];
  keywords: string[];
  weight: number;
}> = [
  {
    intent: 'data_analysis',
    capabilities: ['analytics'],
    patterns: [
      /analyze\s+(my|the|this|our)?\s*(data|sales|revenue|metrics|performance|numbers|stats|kpi)/i,
      /chart|graph|visualize|plot|trend|compare\s+data/i,
      /how\s+(are|is|did|was)\s+(sales|revenue|performance|metrics|numbers)/i,
      /break\s+down|breakdown|segment|distribution/i,
    ],
    keywords: ['analyze', 'chart', 'graph', 'data', 'sales', 'revenue', 'metrics', 'kpi', 'performance', 'trend', 'compare', 'breakdown', 'segment', 'visualize', 'plot', 'statistics'],
    weight: 1.0,
  },
  {
    intent: 'content_creation',
    capabilities: ['content'],
    patterns: [
      /write|draft|create|generate\s+(an?|the)?\s*(email|message|post|blog|article|report|summary|doc|document)/i,
      /help\s+me\s+write|compose|draft\s+an?/i,
      /rewrite|rephrase|polish|improve\s+this/i,
    ],
    keywords: ['write', 'draft', 'email', 'compose', 'create', 'generate', 'blog', 'article', 'report', 'summary', 'document', 'rewrite', 'rephrase', 'polish', 'content'],
    weight: 1.0,
  },
  {
    intent: 'workflow_automation',
    capabilities: ['automation'],
    patterns: [
      /automate|workflow|pipeline|trigger|schedule|cron|recurring/i,
      /when\s+X\s+happen.*do\s+Y|if\s+this\s+then\s+that/i,
      /connect|integrate|sync\s+(with|to)/i,
      /build\s+(a|an)?\s*workflow|create\s+(a|an)?\s*automation/i,
    ],
    keywords: ['automate', 'workflow', 'pipeline', 'trigger', 'schedule', 'cron', 'integrate', 'sync', 'connect', 'bot', 'ifttt', 'zapier'],
    weight: 1.0,
  },
  {
    intent: 'research',
    capabilities: ['research'],
    patterns: [
      /research|find|search|look\s+up|investigate|what\s+is|who\s+is|how\s+does/i,
      /tell\s+me\s+about|explain|what\s+are|compare\s+(.*?)\s+and/i,
      /latest\s+news|recent\s+developments|market\s+trends/i,
    ],
    keywords: ['research', 'find', 'search', 'look up', 'investigate', 'explain', 'compare', 'news', 'trends', 'market', 'industry', 'competitor'],
    weight: 0.9,
  },
  {
    intent: 'security_audit',
    capabilities: ['security'],
    patterns: [
      /security|audit|vulnerability|scan|compliance|gdpr|soc2|penetration\s+test/i,
      /is\s+(this|my|our)\s+(secure|safe|vulnerable|compliant)/i,
      /check\s+security|security\s+check|risk\s+assessment/i,
    ],
    keywords: ['security', 'audit', 'vulnerability', 'scan', 'compliance', 'gdpr', 'risk', 'safe', 'secure', 'penetration', 'threat'],
    weight: 1.0,
  },
  {
    intent: 'ml_model',
    capabilities: ['ml'],
    patterns: [
      /train\s+(a|an)?\s*model|machine\s+learning|ml|predict|classification|regression/i,
      /dataset|feature|training|inference|accuracy|precision|recall|f1/i,
      /build\s+(a|an)?\s*(ai|ml|model)|fine[-\s]?tune/i,
    ],
    keywords: ['train', 'model', 'machine learning', 'ml', 'predict', 'dataset', 'feature', 'accuracy', 'classification', 'regression', 'fine-tune', 'inference'],
    weight: 1.0,
  },
  {
    intent: 'ux_design',
    capabilities: ['ux'],
    patterns: [
      /design|wireframe|mockup|prototype|ui|ux|interface|component|layout/i,
      /create\s+(a|an)?\s*(design|wireframe|mockup|prototype)/i,
      /how\s+should\s+i\s+design|redesign|improve\s+(the|this)\s+ui/i,
    ],
    keywords: ['design', 'wireframe', 'mockup', 'prototype', 'ui', 'ux', 'interface', 'component', 'layout', 'redesign', 'figma', 'screen'],
    weight: 0.9,
  },
  {
    intent: 'market_research',
    capabilities: ['market_research'],
    patterns: [
      /market\s+research|competitor|industry|market\s+size|swot|pestle/i,
      /who\s+are\s+(my|our)\s+competitors|competitive\s+analysis/i,
      /market\s+share|pricing|positioning|target\s+audience/i,
    ],
    keywords: ['market', 'competitor', 'industry', 'swot', 'analysis', 'pricing', 'positioning', 'audience', 'share', 'segment'],
    weight: 0.9,
  },
  {
    intent: 'dashboard_view',
    capabilities: ['dashboard', 'performance'],
    patterns: [
      /dashboard|overview|summary|status|health\s+check|pulse/i,
      /show\s+me\s+(the|my|our)\s+(dashboard|overview|status)/i,
      /what\s+is\s+the\s+current\s+status|how\s+are\s+we\s+doing/i,
    ],
    keywords: ['dashboard', 'overview', 'summary', 'status', 'health', 'pulse', 'current', 'metrics', 'kpi'],
    weight: 0.9,
  },
  {
    intent: 'code_generation',
    capabilities: ['general'],
    patterns: [
      /code|function|script|program|implement|build\s+(a|an)?\s*(app|api|script)/i,
      /write\s+(me|a|an)?\s*(code|function|script|program)/i,
      /debug|fix\s+(this|the|my)\s+code|error|bug/i,
    ],
    keywords: ['code', 'function', 'script', 'program', 'implement', 'debug', 'fix', 'error', 'bug', 'api', 'app'],
    weight: 0.9,
  },
];

// Context memory for multi-turn intent tracking
interface ConversationContext {
  lastIntent: string | null;
  lastCapabilities: Capability[];
  lastEntities: Record<string, any>;
  turnCount: number;
  topics: string[];
}

class IntentDetector {
  private context: ConversationContext = {
    lastIntent: null,
    lastCapabilities: [],
    lastEntities: {},
    turnCount: 0,
    topics: [],
  };

  detect(message: string, conversationHistory: Message[] = []): IntentResult {
    const lowerMessage = message.toLowerCase();
    let bestMatch: { intent: string; capabilities: Capability[]; score: number; entities: Record<string, any> } | null = null;

    // Check for follow-up patterns (pronoun references, short replies)
    const isFollowUp = this.isFollowUpMessage(lowerMessage, conversationHistory);

    for (const pattern of INTENT_PATTERNS) {
      let score = 0;
      const matchedEntities: Record<string, any> = {};

      // Pattern matching
      for (const regex of pattern.patterns) {
        const match = lowerMessage.match(regex);
        if (match) {
          score += pattern.weight * 0.5;
          if (match.groups) Object.assign(matchedEntities, match.groups);
        }
      }

      // Keyword matching
      const matchedKeywords = pattern.keywords.filter(kw => lowerMessage.includes(kw));
      score += (matchedKeywords.length / pattern.keywords.length) * pattern.weight * 0.3;

      // Context boost if this continues previous intent
      if (isFollowUp && this.context.lastIntent === pattern.intent) {
        score += 0.3;
      }

      // Entity extraction
      this.extractEntities(lowerMessage, matchedEntities);

      if (!bestMatch || score > bestMatch.score) {
        bestMatch = {
          intent: pattern.intent,
          capabilities: pattern.capabilities,
          score,
          entities: matchedEntities,
        };
      }
    }

    // Default to general if no strong match
    if (!bestMatch || bestMatch.score < 0.2) {
      bestMatch = {
        intent: 'general_conversation',
        capabilities: ['general'],
        score: 0.1,
        entities: {},
      };
    }

    // Check for multi-capability requests
    const multiCap = this.detectMultiCapability(lowerMessage);
    if (multiCap.length > 1) {
      bestMatch.capabilities = [...new Set([...bestMatch.capabilities, ...multiCap])];
    }

    const result: IntentResult = {
      primaryIntent: bestMatch.intent,
      capabilities: bestMatch.capabilities,
      confidence: Math.min(bestMatch.score, 1.0),
      entities: bestMatch.entities,
      urgency: this.detectUrgency(lowerMessage),
      requiresFollowUp: this.detectFollowUpNeed(lowerMessage, bestMatch.intent),
    };

    // Update context
    this.context.lastIntent = result.primaryIntent;
    this.context.lastCapabilities = result.capabilities;
    this.context.lastEntities = result.entities;
    this.context.turnCount++;
    this.context.topics.push(result.primaryIntent);

    return result;
  }

  private isFollowUpMessage(message: string, history: Message[]): boolean {
    if (history.length === 0) return false;
    const followUpPatterns = /^(and|also|what about|how about|can you|could you|then|next|now|yes|no|ok|okay|sure|please|add|include|remove|delete|update|change|make it|try)/i;
    const isShort = message.split(/\s+/).length < 5;
    return followUpPatterns.test(message) || isShort;
  }

  private detectMultiCapability(message: string): Capability[] {
    const caps: Capability[] = [];
    if (/analyze.*and.*(write|draft|create)/i.test(message)) caps.push('analytics', 'content');
    if (/research.*and.*(write|draft)/i.test(message)) caps.push('research', 'content');
    if (/analyze.*and.*(visualize|chart)/i.test(message)) caps.push('analytics');
    if (/security.*and.*(compliance|audit)/i.test(message)) caps.push('security');
    if (/workflow.*and.*(automate|schedule)/i.test(message)) caps.push('automation');
    return caps;
  }

  private extractEntities(message: string, entities: Record<string, any>): void {
    // Date extraction
    const dateMatch = message.match(/\b(\d{1,2}[\/-]\d{1,2}[\/-]\d{2,4}|\d{4}[\/-]\d{1,2}[\/-]\d{1,2}|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2}(?:,\s+\d{4})?)\b/i);
    if (dateMatch) entities.date = dateMatch[1];

    // Number extraction (for metrics)
    const numberMatch = message.match(/\b(\d+(?:,\d{3})*(?:\.\d+)?)\s*(?:%|percent|k|million|billion)?\b/i);
    if (numberMatch) entities.number = numberMatch[1];

    // File type
    const fileMatch = message.match(/\.(csv|xlsx|pdf|doc|json|xml|txt|png|jpg|jpeg)\b/i);
    if (fileMatch) entities.fileType = fileMatch[1];

    // Email
    const emailMatch = message.match(/[\w.-]+@[\w.-]+\.\w+/i);
    if (emailMatch) entities.email = emailMatch[0];

    // URL
    const urlMatch = message.match(/https?:\/\/[^\s]+/i);
    if (urlMatch) entities.url = urlMatch[0];
  }

  private detectUrgency(message: string): 'low' | 'medium' | 'high' {
    if (/urgent|asap|immediately|now|right away|critical|emergency/i.test(message)) return 'high';
    if (/soon|today|this week|quickly|fast/i.test(message)) return 'medium';
    return 'low';
  }

  private detectFollowUpNeed(message: string, intent: string): boolean {
    const followUpIntents = ['data_analysis', 'research', 'market_research', 'security_audit'];
    return followUpIntents.includes(intent) || /\?$/.test(message);
  }

  resetContext(): void {
    this.context = {
      lastIntent: null,
      lastCapabilities: [],
      lastEntities: {},
      turnCount: 0,
      topics: [],
    };
  }
}

// ============================================================
// CAPABILITY ROUTER
// Simulates agent execution -- in production, this calls actual services
// ============================================================

class CapabilityRouter {
  async execute(capability: Capability, entities: Record<string, any>, message: string): Promise<CapabilityResult> {
    const startTime = Date.now();

    try {
      // Simulate async execution
      await this.simulateLatency(capability);

      const output = await this.mockExecute(capability, entities, message);

      return {
        capability,
        output,
        latency: Date.now() - startTime,
        success: true,
      };
    } catch (error) {
      return {
        capability,
        output: null,
        latency: Date.now() - startTime,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  private async simulateLatency(capability: Capability): Promise<void> {
    const latencies: Record<Capability, number> = {
      analytics: 800,
      content: 600,
      automation: 400,
      research: 1200,
      security: 1500,
      ml: 2000,
      ux: 700,
      market_research: 1000,
      dashboard: 500,
      performance: 600,
      general: 300,
    };
    await new Promise(r => setTimeout(r, latencies[capability] || 500));
  }

  private async mockExecute(capability: Capability, entities: Record<string, any>, message: string): Promise<any> {
    // In production, these would call actual microservices/APIs
    switch (capability) {
      case 'analytics':
        return this.mockAnalytics(entities, message);
      case 'content':
        return this.mockContent(entities, message);
      case 'automation':
        return this.mockAutomation(entities, message);
      case 'research':
        return this.mockResearch(entities, message);
      case 'security':
        return this.mockSecurity(entities, message);
      case 'ml':
        return this.mockML(entities, message);
      case 'ux':
        return this.mockUX(entities, message);
      case 'market_research':
        return this.mockMarketResearch(entities, message);
      case 'dashboard':
      case 'performance':
        return this.mockDashboard(entities, message);
      default:
        return { text: 'General response generated.' };
    }
  }

  private mockAnalytics(entities: Record<string, any>, message: string): any {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return {
      chartType: 'bar',
      labels: months,
      datasets: [
        { label: 'Revenue', data: [120, 135, 148, 162, 175, 190] },
        { label: 'Target', data: [110, 130, 140, 155, 170, 185] },
      ],
      summary: 'Revenue is up 23% vs last quarter. Enterprise renewals are the primary driver (+40%).',
    };
  }

  private mockContent(entities: Record<string, any>, message: string): any {
    return {
      title: 'Draft Email',
      type: 'email',
      to: entities.email || 'team@company.com',
      subject: 'Q2 Performance Summary',
      content: `Hi team,

I wanted to share a quick update on our Q2 performance.

Key highlights:
- Revenue up 23% quarter-over-quarter
- Enterprise renewals drove 40% of growth
- Customer retention at 94%

Let me know if you'd like me to dive deeper into any specific area.

Best,
Miraal`,
    };
  }

  private mockAutomation(entities: Record<string, any>, message: string): any {
    return {
      title: 'Sales Report Automation',
      status: 'Draft',
      steps: [
        { type: 'trigger', name: 'Weekly Schedule', description: 'Every Monday at 9 AM', config: { cron: '0 9 * * 1' } },
        { type: 'action', name: 'Fetch Sales Data', description: 'Pull from CRM', config: { source: 'salesforce', query: 'Q2 opportunities' } },
        { type: 'condition', name: 'Check Threshold', description: 'Revenue > Rs.10L', config: { field: 'revenue', operator: 'gt', value: 1000000 } },
        { type: 'action', name: 'Generate Report', description: 'Create PDF summary', config: { format: 'pdf', template: 'weekly_sales' } },
        { type: 'action', name: 'Send Email', description: 'To leadership team', config: { to: 'leadership@company.com', subject: 'Weekly Sales Report' } },
      ],
    };
  }

  private mockResearch(entities: Record<string, any>, message: string): any {
    return {
      query: message,
      sources: [
        { title: 'AI Agent Market Trends 2026', url: 'https://example.com/ai-trends', domain: 'gartner.com', snippet: 'The AI agent market is projected to reach $45B by 2027...', relevance: 0.95 },
        { title: 'Enterprise AI Adoption Report', url: 'https://example.com/enterprise-ai', domain: 'mckinsey.com', snippet: '67% of enterprises plan to deploy AI agents in 2026...', relevance: 0.88 },
        { title: 'Building Reliable AI Systems', url: 'https://example.com/reliable-ai', domain: 'arxiv.org', snippet: 'New architectures for fault-tolerant agent orchestration...', relevance: 0.82 },
      ],
    };
  }

  private mockSecurity(entities: Record<string, any>, message: string): any {
    return {
      overallScore: 78,
      summary: 'Good security posture with minor improvements needed.',
      checks: [
        { name: 'Data Encryption at Rest', passed: true },
        { name: 'Data Encryption in Transit', passed: true },
        { name: 'Access Control (RBAC)', passed: true },
        { name: 'Audit Logging', passed: false },
        { name: 'API Rate Limiting', passed: true },
        { name: 'Input Validation', passed: false },
        { name: 'Dependency Scanning', passed: true },
      ],
    };
  }

  private mockML(entities: Record<string, any>, message: string): any {
    return {
      modelName: 'Customer Churn Predictor',
      status: 'Training',
      trainingProgress: 73,
      metrics: [
        { name: 'Accuracy', value: '0.89', target: '0.90' },
        { name: 'Precision', value: '0.87', target: '0.85' },
        { name: 'Recall', value: '0.91', target: '0.88' },
      ],
    };
  }

  private mockUX(entities: Record<string, any>, message: string): any {
    return {
      language: 'tsx',
      filename: 'DashboardCard.tsx',
      code: `import React from 'react';

interface DashboardCardProps {
  title: string;
  value: number;
  change: number;
}

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title, value, change
}) => {
  return (
    <div className="p-4 rounded-xl bg-[#1a1a1a] border border-[#2a2a2a]">
      <div className="text-[11px] text-[#bbb] uppercase">{title}</div>
      <div className="text-2xl font-semibold text-white mt-1">
        {value.toLocaleString()}
      </div>
      <div className={\`text-xs mt-1 \${change >= 0 ? 'text-green-400' : 'text-red-400'}\`}>
        {change >= 0 ? '^' : 'v'} {Math.abs(change)}%
      </div>
    </div>
  );
};`,
    };
  }

  private mockMarketResearch(entities: Record<string, any>, message: string): any {
    return {
      sources: [
        { title: 'Competitor Analysis: Synapse AI vs Market', url: 'https://example.com/competitor', domain: 'crunchbase.com', snippet: 'Synapse AI raised $12M Series A. Key differentiator: unified agent orchestration...', relevance: 0.92 },
        { title: 'AI Agent Market Size & Forecast', url: 'https://example.com/market-size', domain: 'grandviewresearch.com', snippet: 'Global AI agent market: $8.2B (2025) to $45B (2030), CAGR 40.7%...', relevance: 0.89 },
      ],
    };
  }

  private mockDashboard(entities: Record<string, any>, message: string): any {
    return {
      kpis: [
        { label: 'Active Users', value: 2847, change: 12.5, format: 'number' },
        { label: 'Revenue (MTD)', value: 1240000, change: 8.3, format: 'currency' },
        { label: 'Churn Rate', value: 4.2, change: -1.5, format: 'percent' },
        { label: 'Avg Session', value: 8.5, change: 5.2, format: 'time' },
      ],
    };
  }
}

// ============================================================
// RESPONSE SYNTHESIZER
// Blends multi-capability outputs into one unified Miraal voice
// ============================================================

class ResponseSynthesizer {
  synthesize(intent: IntentResult, results: CapabilityResult[], originalMessage: string): OrchestratedResponse {
    const successfulResults = results.filter(r => r.success);

    // Build the unified response text
    const text = this.generateText(intent, successfulResults, originalMessage);

    // Determine inline capability to render
    const inlineCapability = this.selectInlineCapability(successfulResults);

    // Generate suggested actions
    const suggestedActions = this.generateSuggestedActions(intent, successfulResults);

    return {
      text,
      inlineCapability,
      suggestedActions,
      capabilitiesUsed: successfulResults.map(r => r.capability),
    };
  }

  private generateText(intent: IntentResult, results: CapabilityResult[], originalMessage: string): string {
    const caps = results.map(r => r.capability);

    // Multi-capability blend
    if (caps.includes('analytics') && caps.includes('content')) {
      return `Here's what I found -- your Q2 revenue is up 23% driven by enterprise renewals (+40%). I've also drafted a summary email to your team if you'd like to review it.`;
    }

    if (caps.includes('research') && caps.includes('content')) {
      return `I researched the latest AI agent market trends and drafted a competitive analysis report. The market is projected to reach $45B by 2027, with enterprise adoption at 67%.`;
    }

    if (caps.includes('analytics') && caps.includes('automation')) {
      return `Your sales data shows strong Q2 performance. I've prepared a workflow that will automatically generate and send this report every Monday at 9 AM.`;
    }

    // Single capability responses
    if (caps.includes('analytics')) {
      return `Here's what I found -- your Q2 revenue is up 23% driven by enterprise renewals. The chart below breaks this down by month.`;
    }

    if (caps.includes('content')) {
      return `I've drafted that email for you. Feel free to edit it before sending.`;
    }

    if (caps.includes('automation')) {
      return `I've built a workflow that triggers every Monday at 9 AM, fetches your sales data, checks if revenue exceeds Rs.10L, generates a report, and emails the leadership team.`;
    }

    if (caps.includes('research')) {
      return `I found some relevant sources on that topic. Here's a summary of the key findings.`;
    }

    if (caps.includes('security')) {
      return `I ran a security audit. Your overall score is 78/100 -- good posture with a couple of areas to improve. I've highlighted the failed checks below.`;
    }

    if (caps.includes('ml')) {
      return `Your Customer Churn Predictor model is 73% through training. Current metrics: Accuracy 89%, Precision 87%, Recall 91%.`;
    }

    if (caps.includes('dashboard') || caps.includes('performance')) {
      return `Here's your current dashboard overview. Active users are up 12.5% and revenue is trending positively.`;
    }

    if (caps.includes('ux')) {
      return `I've generated the component code for you. You can copy it directly into your project.`;
    }

    // General fallback
    return `I understand. Let me help you with that.`;
  }

  private selectInlineCapability(results: CapabilityResult[]): any | undefined {
    // Priority order for inline rendering
    const priority = ['analytics', 'table', 'code', 'automation', 'security', 'ml', 'dashboard', 'research', 'content'];

    for (const cap of priority) {
      const result = results.find(r => r.capability === cap || (cap === 'table' && r.capability === 'analytics'));
      if (result && result.output) {
        const typeMap: Record<string, string> = {
          analytics: 'analytics',
          automation: 'automation',
          security: 'security',
          ml: 'ml',
          dashboard: 'performance',
          performance: 'performance',
          research: 'research',
          content: 'content',
          ux: 'code',
        };
        return {
          type: typeMap[cap] || cap,
          data: result.output,
        };
      }
    }

    return undefined;
  }

  private generateSuggestedActions(intent: IntentResult, results: CapabilityResult[]): Array<{ id: string; label: string; action: string }> {
    const actions: Array<{ id: string; label: string; action: string }> = [];

    const caps = results.map(r => r.capability);

    if (caps.includes('analytics')) {
      actions.push({ id: 'breakdown', label: 'Break down by region', action: 'Break down the revenue data by region' });
      actions.push({ id: 'export', label: 'Export as CSV', action: 'Export this data as CSV' });
    }

    if (caps.includes('content')) {
      actions.push({ id: 'edit', label: 'Edit draft', action: 'Let me edit the draft' });
      actions.push({ id: 'send', label: 'Send now', action: 'Send the email now' });
    }

    if (caps.includes('automation')) {
      actions.push({ id: 'activate', label: 'Activate workflow', action: 'Activate this workflow' });
      actions.push({ id: 'test', label: 'Test run', action: 'Run a test of this workflow' });
    }

    if (caps.includes('research')) {
      actions.push({ id: 'deep-dive', label: 'Deep dive', action: 'Tell me more about the top finding' });
      actions.push({ id: 'compare', label: 'Compare sources', action: 'Compare the top two sources' });
    }

    if (caps.includes('security')) {
      actions.push({ id: 'fix', label: 'Fix issues', action: 'Help me fix the failed security checks' });
      actions.push({ id: 'schedule', label: 'Schedule audit', action: 'Schedule weekly security audits' });
    }

    if (caps.includes('ml')) {
      actions.push({ id: 'improve', label: 'Improve model', action: 'How can I improve the model accuracy?' });
      actions.push({ id: 'deploy', label: 'Deploy', action: 'Deploy this model to production' });
    }

    if (actions.length === 0) {
      actions.push({ id: 'more', label: 'Tell me more', action: 'Tell me more about this' });
      actions.push({ id: 'help', label: 'What else can you do?', action: 'What else can you help me with?' });
    }

    return actions;
  }
}

// ============================================================
// REACT CONTEXT & HOOK
// ============================================================

interface OrchestratorContextType {
  state: OrchestratorState;
  processMessage: (message: string, history: Message[]) => Promise<OrchestratedResponse>;
  reset: () => void;
}

const OrchestratorContext = createContext<OrchestratorContextType | null>(null);

export const OrchestratorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OrchestratorState>({
    isProcessing: false,
    currentIntent: null,
    activeCapabilities: [],
    lastResponse: null,
    error: null,
  });

  const detectorRef = useRef(new IntentDetector());
  const routerRef = useRef(new CapabilityRouter());
  const synthesizerRef = useRef(new ResponseSynthesizer());

  const processMessage = useCallback(async (message: string, history: Message[]): Promise<OrchestratedResponse> => {
    setState(prev => ({ ...prev, isProcessing: true, error: null }));

    try {
      // Step 1: Detect intent (silent, no UI indication)
      const intent = detectorRef.current.detect(message, history);

      setState(prev => ({
        ...prev,
        currentIntent: intent,
        activeCapabilities: intent.capabilities,
      }));

      // Step 2: Route to capabilities (parallel execution)
      const results = await Promise.all(
        intent.capabilities.map(cap =>
          routerRef.current.execute(cap, intent.entities, message)
        )
      );

      // Step 3: Synthesize unified response
      const response = synthesizerRef.current.synthesize(intent, results, message);

      setState(prev => ({
        ...prev,
        isProcessing: false,
        lastResponse: response,
        activeCapabilities: [],
      }));

      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Processing failed';
      setState(prev => ({
        ...prev,
        isProcessing: false,
        error: errorMessage,
        activeCapabilities: [],
      }));
      throw error;
    }
  }, []);

  const reset = useCallback(() => {
    detectorRef.current.resetContext();
    setState({
      isProcessing: false,
      currentIntent: null,
      activeCapabilities: [],
      lastResponse: null,
      error: null,
    });
  }, []);

  return (
    <OrchestratorContext.Provider value={{ state, processMessage, reset }}>
      {children}
    </OrchestratorContext.Provider>
  );
};

export const useOrchestrator = (): OrchestratorContextType => {
  const context = useContext(OrchestratorContext);
  if (!context) {
    throw new Error('useOrchestrator must be used within OrchestratorProvider');
  }
  return context;
};